import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Bell, CalendarPlus, Clock, MapPin, Pencil, Radio, Users, X, Trash2, Link2, GripVertical, Sparkles } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { ToastAction } from '../../components/ui/toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'stream', label: '📡 Stream' },
  { value: 'nightclub', label: '🪩 Nightclub' },
  { value: 'bar', label: '🍸 Bar' },
  { value: 'lounge', label: '🛋️ Lounge' },
  { value: 'private', label: '🎉 Private Event' },
  { value: 'other', label: '✨ Other' },
];
const TYPE_LABELS = Object.fromEntries(EVENT_TYPES.map(t => [t.value, t.label]));

const TZ_OFFSETS = { EST: -5, CST: -6, MST: -7, PST: -8, UTC: 0, GMT: 0, CET: 1, BST: 1, IST: 5.5, JST: 9, AEST: 10 };
const TZ_CHOICES = Object.keys(TZ_OFFSETS);

const fmt12 = (ms) => {
  const d = new Date(ms);
  let h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ap}`;
};

// Is this event's date/time known enough to do timezone math?
const eventHasRealTime = (ev) => !ev?.date_tbd && !ev?.time_tbd && ev?.event_date && ev?.start_time;

// Convert a slot's times into the viewer's chosen timezone
const slotTimesInTz = (event, slot, viewTz) => {
  if (!eventHasRealTime(event)) {
    // TBD event — labels are placeholder text, no shifting
    return { start: slot.start_label, end: slot.end_label, tz: '', shift: 0, tbd: true };
  }
  if (!viewTz || viewTz === 'event') {
    return { start: slot.start_label, end: slot.end_label, tz: event.timezone_label, shift: 0 };
  }
  const [y, mo, d] = event.event_date.split('-').map(Number);
  const [hh, mm] = event.start_time.split(':').map(Number);
  const evOff = TZ_OFFSETS[event.timezone_label] ?? -5;
  const baseUtc = Date.UTC(y, mo - 1, d, hh, mm) - evOff * 3600000;
  const dur = (event.slot_duration || 60) * 60000;
  const viewOff = viewTz === 'local' ? -new Date().getTimezoneOffset() / 60 : (TZ_OFFSETS[viewTz] ?? 0);
  const s = baseUtc + slot.index * dur + viewOff * 3600000;
  const e = s + dur;
  const sd = new Date(s);
  const shift = Math.round((Date.UTC(sd.getUTCFullYear(), sd.getUTCMonth(), sd.getUTCDate()) - Date.UTC(y, mo - 1, d)) / 86400000);
  return { start: fmt12(s), end: fmt12(e), tz: viewTz === 'local' ? 'your time' : viewTz, shift };
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('beatseek_token')}`,
});

const getMe = () => {
  try { return JSON.parse(localStorage.getItem('beatseek_user') || 'null'); } catch { return null; }
};
const typeTokens = (u) => new Set((u?.user_type || '').split(',').map(t => t.trim()).filter(Boolean));

const inputCls = 'w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500';
const labelCls = 'block text-xs font-semibold text-purple-300 mb-1';

// Convert an event's start date+time into the viewer's chosen timezone
const eventStartInTz = (ev, viewTz) => {
  // TBD-aware short-circuit
  if (!ev?.event_date || ev?.date_tbd) {
    return { dateStr: '📅 Coming soon', time: '', tz: '', tbd: true };
  }
  const [y, mo, d] = ev.event_date.split('-').map(Number);
  const buildRangeStr = (startDate) => {
    const startFmt = startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (ev.event_end_date && ev.event_end_date !== ev.event_date) {
      try {
        const [ey, em, ed] = ev.event_end_date.split('-').map(Number);
        const endFmt = new Date(Date.UTC(ey, em - 1, ed, 12)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        return `${startFmt} – ${endFmt}`;
      } catch { /* noop */ }
    }
    return startFmt;
  };

  if (!ev.start_time || ev.time_tbd) {
    const startDate = new Date(`${ev.event_date}T12:00:00`);
    return { dateStr: buildRangeStr(startDate), time: 'time TBD', tz: '', tbd: true };
  }
  if (!viewTz || viewTz === 'event') {
    const startDate = new Date(`${ev.event_date}T12:00:00`);
    return { dateStr: buildRangeStr(startDate), time: ev.slots?.[0]?.start_label || '', tz: ev.timezone_label };
  }
  const [hh, mm] = ev.start_time.split(':').map(Number);
  const evOff = TZ_OFFSETS[ev.timezone_label] ?? -5;
  const viewOff = viewTz === 'local' ? -new Date().getTimezoneOffset() / 60 : (TZ_OFFSETS[viewTz] ?? 0);
  const ms = Date.UTC(y, mo - 1, d, hh, mm) - evOff * 3600000 + viewOff * 3600000;
  const dt = new Date(ms);
  const dateStr = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 12)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  return { dateStr, time: fmt12(ms), tz: viewTz === 'local' ? 'your time' : viewTz };
};

// ── Create / Edit Event Modal ──────────────────────────────
const CreateEventModal = ({ onClose, onCreated, initial = null, eventId = null }) => {
  const isEdit = !!eventId;
  const [form, setForm] = useState(() => initial ? {
    title: initial.title || '', event_type: initial.event_type || 'stream',
    event_date: initial.event_date || '', event_end_date: initial.event_end_date || '',
    start_time: initial.start_time || '21:00', timezone_label: initial.timezone_label || 'EST',
    date_tbd: !!initial.date_tbd, time_tbd: !!initial.time_tbd, schedule_type: initial.schedule_type || null,
    description: initial.description || '', genres: initial.genres || '',
    venue_name: initial.venue_name || '', city: initial.city || '', address: initial.address || '',
    hide_venue: !!initial.hide_venue, platform: initial.platform || '',
    channel_link: initial.channel_link || '', flyer_url: initial.flyer_url || '',
    external_link: initial.external_link || '', discord_link: initial.discord_link || '',
    days: initial.days || Math.max(1, Math.min(7, Math.ceil((initial.slots?.length || 24) / 24))),
    claim_mode: initial.claim_mode || 'open', wall_tone: 'standard',
  } : {
    title: '', event_type: 'stream',
    event_date: '', event_end_date: '', start_time: '21:00', timezone_label: 'EST',
    date_tbd: false, time_tbd: false, schedule_type: null,
    description: '', genres: '', venue_name: '', city: '', address: '', hide_venue: false,
    platform: '', channel_link: '', flyer_url: '', external_link: '', discord_link: '',
    days: 1, claim_mode: 'open', wall_tone: 'standard',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // While the form is open, hide the fixed footer/nav & badges so they can't cover the Publish button
  useEffect(() => {
    document.body.classList.add('planner-form-open');
    return () => document.body.classList.remove('planner-form-open');
  }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleScheduleFlag = (flag) => {
    setForm(f => {
      const cur = f.schedule_type;
      let next;
      if (flag === 'weekday') {
        next = cur === 'weekday' ? null : cur === 'weekend' ? 'both' : cur === 'both' ? 'weekend' : 'weekday';
      } else {
        next = cur === 'weekend' ? null : cur === 'weekday' ? 'both' : cur === 'both' ? 'weekday' : 'weekend';
      }
      return { ...f, schedule_type: next };
    });
  };
  const scheduleHas = (flag) => form.schedule_type === flag || form.schedule_type === 'both';

  const submit = async () => {
    if (!form.title.trim()) {
      setError('Event title is required');
      return;
    }
    if (!form.date_tbd && !form.event_date) {
      setError('Set a start date, or toggle "Date TBD" if it isn\'t locked in yet');
      return;
    }
    if (!form.time_tbd && !form.start_time) {
      setError('Set a start time, or toggle "Time TBD" if it isn\'t locked in yet');
      return;
    }
    if (form.event_end_date && form.event_date && form.event_end_date < form.event_date) {
      setError('End date must be on or after the start date');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        event_date: form.date_tbd ? null : form.event_date,
        event_end_date: form.date_tbd ? null : (form.event_end_date || null),
        start_time: form.time_tbd ? null : form.start_time,
        schedule_type: form.schedule_type,
        days: Number(form.days) || 1,
      };
      const res = await fetch(isEdit ? `${BACKEND_URL}/api/planner/events/${eventId}` : `${BACKEND_URL}/api/planner/events`, {
        method: isEdit ? 'PUT' : 'POST', headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) { onCreated(data.event); }
      else { setError(data.detail || (isEdit ? 'Failed to save changes' : 'Failed to create event')); }
    } catch { setError(isEdit ? 'Failed to save changes' : 'Failed to create event'); }
    setSaving(false);
  };

  const tbdBtnCls = (on) => `text-[11px] px-2.5 py-1 rounded-lg border font-medium transition-colors ${on ? 'bg-amber-500/25 border-amber-400 text-amber-100' : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:border-amber-500/50 hover:text-amber-200'}`;
  const chipCls = (on) => `text-xs px-3 py-1.5 rounded-lg border font-semibold transition-colors ${on ? 'bg-cyan-500/25 border-cyan-400 text-cyan-100' : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:border-cyan-500/50 hover:text-cyan-200'}`;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[10001] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="planner-create-modal">
      <div className="w-full max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col bg-[#141428] border border-purple-500/40 rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 pb-3 border-b border-purple-500/20 shrink-0">
          <h3 className="text-white text-lg font-bold">{isEdit ? '✏️ Edit Event' : '🗓️ Plan a New Event'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="planner-create-close"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className={labelCls}>Event Title *</label>
              <input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g., BEATSEEK Gates Open Party" data-testid="planner-title-input" />
            </div>
            <div>
              <label className={labelCls}>Event Type *</label>
              <select className={inputCls} value={form.event_type} onChange={e => set('event_type', e.target.value)} data-testid="planner-type-select">
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Genres</label>
              <input className={inputCls} value={form.genres} onChange={e => set('genres', e.target.value)} placeholder="House, Techno…" />
            </div>
          </div>

          {/* When block — dates, TBD, schedule chips */}
          <div className="bg-amber-900/10 border border-amber-700/30 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs font-semibold text-amber-300">🗓️ When</div>
              <div className="flex gap-1.5 flex-wrap">
                <button type="button" onClick={() => set('date_tbd', !form.date_tbd)} className={tbdBtnCls(form.date_tbd)} data-testid="planner-date-tbd-btn">
                  {form.date_tbd ? '✓ Date TBD' : '📅 Date TBD'}
                </button>
                <button type="button" onClick={() => set('time_tbd', !form.time_tbd)} className={tbdBtnCls(form.time_tbd)} data-testid="planner-time-tbd-btn">
                  {form.time_tbd ? '✓ Time TBD' : '🕘 Time TBD'}
                </button>
              </div>
            </div>

            {form.date_tbd ? (
              <div className="text-gray-400 text-xs italic px-1" data-testid="planner-date-tbd-notice">📅 Date: coming soon — DJs can still browse and mark interest.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Start Date</label>
                  <input type="date" className={inputCls} value={form.event_date} onChange={e => set('event_date', e.target.value)} data-testid="planner-date-input" />
                </div>
                <div>
                  <label className={labelCls}>End Date <span className="text-gray-500 font-normal">(optional — multi-day)</span></label>
                  <input type="date" className={inputCls} value={form.event_end_date} min={form.event_date || undefined} onChange={e => set('event_end_date', e.target.value)} data-testid="planner-end-date-input" />
                </div>
              </div>
            )}

            {form.time_tbd ? (
              <div className="text-gray-400 text-xs italic px-1" data-testid="planner-time-tbd-notice">🕘 Start time: coming soon — slots will show as &quot;Slot 1&quot;, &quot;Slot 2&quot;… until you set a time.</div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className={labelCls}>Start Time</label>
                  <input type="time" className={inputCls} value={form.start_time} onChange={e => set('start_time', e.target.value)} data-testid="planner-time-input" />
                </div>
                <div className="w-24">
                  <label className={labelCls}>Timezone</label>
                  <select className={inputCls} value={form.timezone_label} onChange={e => set('timezone_label', e.target.value)}>
                    {TZ_CHOICES.map(tz => <option key={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div>
              <div className="text-[11px] font-semibold text-gray-400 mb-1.5">Schedule fit <span className="text-gray-600 font-normal">(optional — combine for week-long events)</span></div>
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => toggleScheduleFlag('weekday')} className={chipCls(scheduleHas('weekday'))} data-testid="planner-weekday-chip">
                  {scheduleHas('weekday') ? '✓ Weekday' : 'Weekday'}
                </button>
                <button type="button" onClick={() => toggleScheduleFlag('weekend')} className={chipCls(scheduleHas('weekend'))} data-testid="planner-weekend-chip">
                  {scheduleHas('weekend') ? '✓ Weekend' : 'Weekend'}
                </button>
                {form.schedule_type === 'both' && <span className="text-cyan-300 text-[11px] self-center">🔁 Runs all week</span>}
              </div>
            </div>
          </div>

          {/* Venue / Platform block */}
          <div className="bg-purple-900/10 border border-purple-700/30 rounded-lg p-3 space-y-3">
            {form.event_type === 'stream' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Platform</label>
                  <input className={inputCls} value={form.platform} onChange={e => set('platform', e.target.value)} placeholder="Twitch, Kick, YouTube…" />
                </div>
                <div>
                  <label className={labelCls}>Channel Link</label>
                  <input className={inputCls} value={form.channel_link} onChange={e => set('channel_link', e.target.value)} placeholder="https://twitch.tv/…" />
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Venue Name</label>
                    <input className={inputCls} value={form.venue_name} onChange={e => set('venue_name', e.target.value)} placeholder="Club Noir" data-testid="planner-venue-input" />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input className={inputCls} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Los Angeles, CA" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Address (optional)</label>
                    <input className={inputCls} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St…" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={form.hide_venue} onChange={e => set('hide_venue', e.target.checked)} className="w-4 h-4" data-testid="planner-hide-venue-check" />
                  🔒 Hide venue details publicly — only confirmed lineup DJs can see them
                </label>
              </>
            )}
          </div>

          <div>
            <label className={labelCls}>Description / Vibe</label>
            <textarea rows={3} className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell DJs about the event, the vibe, what to expect…" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Flyer Image URL</label>
              <input className={inputCls} value={form.flyer_url} onChange={e => set('flyer_url', e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <label className={labelCls}>Event / Ticket Link</label>
              <input className={inputCls} value={form.external_link} onChange={e => set('external_link', e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <label className={labelCls}>Discord Link</label>
              <input className={inputCls} value={form.discord_link} onChange={e => set('discord_link', e.target.value)} placeholder="https://discord.gg/…" />
            </div>
          </div>

          {/* Slot config — day-based: each day = 24 one-hour slots */}
          <div className="bg-cyan-900/10 border border-cyan-700/30 rounded-lg p-3">
            <div className="text-xs font-semibold text-cyan-300 mb-2">🎛️ DJ Slot Setup</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Event Days (max 7)</label>
                {(!form.date_tbd && form.event_date && form.event_end_date) ? (
                  <div className="text-cyan-200 text-sm bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2" data-testid="planner-days-derived">
                    {(() => {
                      const span = Math.floor((new Date(`${form.event_end_date}T12:00:00`) - new Date(`${form.event_date}T12:00:00`)) / 86400000) + 1;
                      const d = Math.max(1, Math.min(7, span));
                      return `${d} day${d === 1 ? '' : 's'} (from your dates) → ${d * 24} slots`;
                    })()}
                  </div>
                ) : (
                  <select className={inputCls} value={form.days} onChange={e => set('days', e.target.value)} data-testid="planner-days-select">
                    {[1, 2, 3, 4, 5, 6, 7].map(d => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'} — {d * 24} slots</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className={labelCls}>Claiming</label>
                <select className={inputCls} value={form.claim_mode} onChange={e => set('claim_mode', e.target.value)} data-testid="planner-claim-mode-select">
                  <option value="open">🟢 Open — instant</option>
                  <option value="request">🟡 Request — I approve</option>
                  <option value="invite">🔒 Invite-only — I assign</option>
                </select>
              </div>
            </div>
            <p className="text-gray-500 text-[11px] mt-2">Each day = a 24-hour block of one-hour slots. DJs can claim up to 2 slots per day — on multi-day events they can grab more on other days.</p>
          </div>

          {!isEdit && (
          <div>
            <label className={labelCls}>📣 Wall Announcement Style</label>
            <select className={inputCls} value={form.wall_tone} onChange={e => set('wall_tone', e.target.value)} data-testid="planner-wall-tone-select">
              <option value="standard">📋 Standard template</option>
              <option value="auto">✨ AI — match my event's vibe</option>
              <option value="hype">🔥 AI — Hype</option>
              <option value="professional">🎩 AI — Professional</option>
              <option value="chill">😎 AI — Chill</option>
            </select>
            <p className="text-gray-500 text-[11px] mt-1">Your event gets announced on The Wall when published — pick who writes it.</p>
          </div>
          )}

          {error && <div className="text-red-400 text-sm" data-testid="planner-create-error">{error}</div>}
          </div>
        </div>

        <div className="shrink-0 border-t border-purple-500/20 bg-[#141428] px-4 sm:px-6 py-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <button
            onClick={submit}
            disabled={saving}
            className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm"
            data-testid="planner-create-submit"
          >
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? '💾 Save Changes' : '🗓️ Publish Event')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Invite Link modal (host) — editable message + AI writer ─
const InviteLinkModal = ({ event, dateStr, me, onClose }) => {
  const link = `${window.location.origin}/?invite=${event.invite_token}`;
  const linkLine = `\nClaim your slot here (this link gets you straight in): ${link}`;
  const timeTbd = event.time_tbd || !event.start_time;
  const dateTbd = event.date_tbd || !event.event_date;
  const whenLine = dateTbd
    ? '📅 Date & time coming soon'
    : timeTbd
      ? `📅 ${dateStr} · 🕘 time coming soon`
      : `📅 ${dateStr} · ${event.slots?.[0]?.start_label || ''} ${event.timezone_label || ''}`;
  const standardMsg = `🎛️ You're invited to play "${event.title}" on BEATSEEK!\n${whenLine}${linkLine}`;
  const grandMsg = `🎛️ You're invited to play the GRAND OPENING of BEATSEEK — the world's first social platform built for DJs, streamers, producers, label execs & electronic music lovers alike. The flood gates are opening, and we want YOU on the lineup. Thank you for being part of this worldwide music journey! 🌍\nClaim your slot (this link gets you straight in): ${link}`;
  const [message, setMessage] = useState(standardMsg);
  const [copied, setCopied] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [aiTone, setAiTone] = useState('auto');
  const [aiNotes, setAiNotes] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const isAdmin = me?.role === 'admin';

  const generateAi = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${event.id}/promo/ai`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ tone: aiTone, notes: aiNotes }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`${data.promo}${linkLine}`);
      } else {
        setAiError(data.detail || 'Generation failed — try again');
      }
    } catch { setAiError('Generation failed — try again'); }
    setAiLoading(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(message).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => window.prompt('Copy your invite message:', message)
    );
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="planner-invite-modal">
      <div className="w-full max-w-lg max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-[#141428] border border-amber-500/40 rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-amber-500/20 shrink-0">
          <h3 className="text-white font-bold text-sm">🔗 Invite Message</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
        <p className="text-gray-400 text-xs mb-3">Edit the message, then copy & paste it anywhere — the link gets them straight past the gate to sign up and claim a slot.</p>
        <div className="flex gap-2 mb-2 flex-wrap">
          <button onClick={() => { setShowAi(false); setMessage(standardMsg); }} className="text-[11px] px-2.5 py-1 rounded-lg bg-gray-700/60 hover:bg-gray-700 text-gray-200 font-medium" data-testid="planner-invite-preset-standard">Standard</button>
          <button onClick={() => setShowAi(s => !s)} className={`text-[11px] px-2.5 py-1 rounded-lg border font-medium ${showAi ? 'bg-cyan-600/50 border-cyan-400/70 text-white' : 'bg-cyan-600/25 hover:bg-cyan-600/40 border-cyan-500/50 text-cyan-200'}`} data-testid="planner-invite-preset-ai">✨ AI Write It</button>
          {isAdmin && (
            <button onClick={() => { setShowAi(false); setMessage(grandMsg); }} className="text-[11px] px-2.5 py-1 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/50 text-amber-200 font-medium" data-testid="planner-invite-preset-grand">🎉 Grand Opening</button>
          )}
        </div>

        {showAi && (
          <div className="bg-cyan-900/10 border border-cyan-700/30 rounded-lg p-3 mb-2 space-y-2" data-testid="planner-invite-ai-panel">
            <div className="flex gap-1.5 flex-wrap">
              {[
                { v: 'auto', l: '✨ Just write it' },
                { v: 'hype', l: '🔥 Hype' },
                { v: 'professional', l: '🎩 Professional' },
                { v: 'chill', l: '😎 Chill' },
              ].map(t => (
                <button
                  key={t.v}
                  onClick={() => setAiTone(t.v)}
                  className={`text-[11px] px-2.5 py-1 rounded-lg border font-medium ${aiTone === t.v ? 'bg-cyan-500/40 border-cyan-400 text-white' : 'bg-gray-800/60 border-gray-700/50 text-gray-300 hover:border-cyan-500/50'}`}
                  data-testid={`planner-invite-tone-${t.v}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
            <input
              className={inputCls}
              value={aiNotes}
              onChange={e => setAiNotes(e.target.value)}
              placeholder="Anything to emphasize? (optional — e.g., free drinks, charity event…)"
              data-testid="planner-invite-ai-notes"
            />
            {aiError && <div className="text-red-400 text-xs">{aiError}</div>}
            <button
              onClick={generateAi}
              disabled={aiLoading}
              className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg"
              data-testid="planner-invite-ai-generate"
            >
              {aiLoading ? '✍️ Writing…' : (message !== standardMsg && message !== grandMsg ? '↻ Regenerate' : '✨ Generate Promo')}
            </button>
          </div>
        )}

        <textarea
          rows={7}
          className={inputCls}
          value={message}
          onChange={e => setMessage(e.target.value)}
          data-testid="planner-invite-message-input"
        />
        </div>
        <div className="shrink-0 border-t border-amber-500/20 bg-[#141428] px-5 py-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <button
          onClick={copy}
          className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold py-2.5 rounded-lg text-sm"
          data-testid="planner-invite-copy-btn"
        >
          {copied ? '✅ Copied!' : '📋 Copy Message'}
        </button>
        </div>
      </div>
    </div>
  );
};

// ── AI Poster modal (host) ─────────────────────────────────
const AiPosterModal = ({ eventId, onClose }) => {
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [error, setError] = useState('');

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('prompt', prompt);
      files.forEach(f => fd.append('files', f));
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/poster/ai`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('beatseek_token')}` },
        body: fd,
      });
      if (res.ok) {
        const blob = await res.blob();
        setResultUrl(URL.createObjectURL(blob));
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Generation failed — please try again');
      }
    } catch { setError('Generation failed — please try again'); }
    setGenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4" onClick={() => !generating && onClose()} data-testid="planner-ai-poster-modal">
      <div className="w-full max-w-lg max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-[#141428] border border-cyan-500/40 rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-cyan-500/20 shrink-0">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><Sparkles size={16} className="text-cyan-400" /> AI Lineup Poster</h3>
          {!generating && <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
        {resultUrl ? (
          <img src={resultUrl} alt="AI lineup poster" className="w-full rounded-lg border border-gray-700/50" data-testid="planner-ai-poster-result" />
        ) : generating ? (
          <div className="text-center py-10" data-testid="planner-ai-poster-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-4"></div>
            <p className="text-white text-sm font-medium">Generating your poster artwork…</p>
            <p className="text-gray-500 text-xs mt-1">This can take up to a minute. Don't close this window.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-400 text-xs">AI paints the artwork from your event's vibe, then your title, lineup and logos are laid over it in crisp BEATSEEK styling.</p>
            <div>
              <label className={labelCls}>Style direction (optional — leave blank for auto)</label>
              <textarea rows={2} className={inputCls} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g., retro 80s Miami vibes, palm trees and chrome…" data-testid="planner-ai-prompt-input" />
            </div>
            <div>
              <label className={labelCls}>Logos / photos (optional, up to 3)</label>
              <input
                type="file" accept="image/*" multiple
                onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 3))}
                className="w-full text-xs text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-cyan-600 file:text-white file:text-xs file:font-semibold hover:file:bg-cyan-500 file:cursor-pointer"
                data-testid="planner-ai-files-input"
              />
              {files.length > 0 && (
                <div className="text-gray-500 text-[11px] mt-1">{files.map(f => f.name).join(' · ')} — first image goes top-right, extras in the footer</div>
              )}
            </div>
            {error && <div className="text-red-400 text-xs" data-testid="planner-ai-poster-error">{error}</div>}
          </div>
        )}
        </div>

        {!generating && (
          <div className="shrink-0 border-t border-cyan-500/20 bg-[#141428] px-5 py-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
            {resultUrl ? (
              <div className="flex gap-2">
                <a href={resultUrl} download={`beatseek-ai-poster.png`} className="flex-1 text-center bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold py-2.5 rounded-lg">⬇️ Download</a>
                <button onClick={() => setResultUrl(null)} className="flex-1 bg-gray-700/60 hover:bg-gray-700 text-white text-sm font-semibold py-2.5 rounded-lg">↻ Try Again</button>
              </div>
            ) : (
              <button onClick={generate} className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-semibold py-2.5 rounded-lg text-sm" data-testid="planner-ai-generate-btn">
                ✨ Generate Poster
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Assign DJ modal (host) ─────────────────────────────────
const AssignModal = ({ slot, onClose, onAssign, busy }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users?query=${encodeURIComponent(val.trim())}&limit=8`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.users || []);
        setResults(list.filter(u => {
          const tokens = (u.user_type || '').split(',').map(t => t.trim());
          return u.role === 'admin' || tokens.includes('dj') || tokens.includes('streamer');
        }));
      }
    } catch { /* noop */ }
    setSearching(false);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[10002] flex items-center justify-center p-4" onClick={onClose} data-testid="planner-assign-modal">
      <div className="w-full max-w-md bg-[#141428] border border-purple-500/40 rounded-2xl p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-sm">Assign the {slot.start_label} slot</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <input
          autoFocus
          className={inputCls}
          value={q}
          onChange={e => search(e.target.value)}
          placeholder="Search DJs / Streamers by name or email…"
          data-testid="planner-assign-search"
        />
        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
          {searching && <div className="text-gray-500 text-xs py-2">Searching…</div>}
          {!searching && results.map(u => (
            <button
              key={u.id}
              onClick={() => onAssign(u)}
              disabled={busy}
              className="w-full flex items-center gap-2.5 bg-gray-800/50 hover:bg-purple-800/30 border border-gray-700/40 rounded-lg px-3 py-2 text-left disabled:opacity-40"
              data-testid={`planner-assign-user-${u.id}`}
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0">
                {u.profile_picture ? <img src={u.profile_picture} alt={u.name} className="w-full h-full object-cover" /> : (u.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{u.name}</div>
                <div className="text-gray-500 text-[11px] truncate">{u.user_type}</div>
              </div>
            </button>
          ))}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="text-gray-500 text-xs py-2">No members found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Slot row ───────────────────────────────────────────────
const SlotRow = ({ slot, event, me, canClaim, busy, viewTz, onClaim, onApprove, onDeny, onRelease, onAssignOpen, dragHandlers }) => {
  const isHost = me && (event.host_id === me.user_id || me.role === 'admin');
  const isMine = me && slot.dj_id === me.user_id;
  const pill = slot.status === 'available'
    ? <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-green-500/15 border border-green-500/50 text-green-300">OPEN</span>
    : slot.status === 'pending'
      ? <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-yellow-500/15 border border-yellow-500/50 text-yellow-300">PENDING</span>
      : <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-fuchsia-500/15 border border-fuchsia-500/50 text-fuchsia-300">CLAIMED</span>;

  return (
    <div
      className="flex items-center gap-3 bg-gray-800/40 border border-gray-700/40 rounded-lg px-3 py-2.5 flex-wrap"
      data-testid={`planner-slot-${slot.index}`}
      draggable={isHost}
      onDragStart={isHost ? () => dragHandlers.onDragStart(slot.index) : undefined}
      onDragOver={isHost ? (e) => e.preventDefault() : undefined}
      onDrop={isHost ? () => dragHandlers.onDrop(slot.index) : undefined}
    >
      {isHost && (
        <span className="text-gray-600 hover:text-purple-300 cursor-grab active:cursor-grabbing shrink-0 -ml-1" title="Drag to move this slot" data-testid={`planner-drag-${slot.index}`}>
          <GripVertical size={16} />
        </span>
      )}
      <span className="text-gray-500 text-sm font-bold w-6 text-center shrink-0">{(slot.index % 24) + 1}</span>
      {pill}
      <div className="flex items-center gap-1.5 text-gray-300 text-xs shrink-0">
        <Clock size={12} className="text-cyan-400" />
        {(() => {
          const t = slotTimesInTz(event, slot, viewTz);
          if (t.tbd) {
            return (
              <span data-testid={`planner-slot-time-${slot.index}`} className="italic text-gray-400">
                {t.start || `Slot ${slot.index + 1}`} <span className="text-amber-400 not-italic font-semibold">· TBD</span>
              </span>
            );
          }
          return (
            <span data-testid={`planner-slot-time-${slot.index}`}>
              {t.start} – {t.end} {t.tz}
              {t.shift !== 0 && <span className="text-amber-400 font-semibold ml-1">({t.shift > 0 ? '+' : ''}{t.shift}d)</span>}
            </span>
          );
        })()}
      </div>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {slot.dj_name ? (
          <>
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0">
              {slot.dj_avatar ? <img src={slot.dj_avatar} alt={slot.dj_name} className="w-full h-full object-cover" /> : (slot.dj_name || '?').charAt(0).toUpperCase()}
            </div>
            <span className="text-white text-sm font-medium truncate">{slot.dj_name}</span>
          </>
        ) : (
          <span className="text-gray-500 text-xs italic">This slot is open</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {slot.status === 'available' && canClaim && event.claim_mode !== 'invite' && (
          <button onClick={() => onClaim(slot.index)} disabled={busy} className="bg-green-500/15 hover:bg-green-500/25 border border-green-500/50 text-green-200 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-semibold" data-testid={`planner-claim-${slot.index}`}>
            {event.claim_mode === 'open' ? 'Claim Slot' : 'Request Slot'}
          </button>
        )}
        {slot.status === 'available' && event.claim_mode === 'invite' && !isHost && (
          <span className="text-gray-500 text-[11px]">🔒 Invite-only</span>
        )}
        {slot.status === 'available' && isHost && (
          <button onClick={() => onAssignOpen(slot)} disabled={busy} className="bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/50 text-purple-200 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-semibold" data-testid={`planner-assign-${slot.index}`}>
            Assign
          </button>
        )}
        {slot.status === 'pending' && isHost && (
          <>
            <button onClick={() => onApprove(slot.index)} disabled={busy} className="bg-green-500/15 hover:bg-green-500/25 border border-green-500/50 text-green-200 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-semibold" data-testid={`planner-approve-${slot.index}`}>Approve</button>
            <button onClick={() => onDeny(slot.index)} disabled={busy} className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-200 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-semibold" data-testid={`planner-deny-${slot.index}`}>Deny</button>
          </>
        )}
        {slot.status === 'pending' && isMine && !isHost && (
          <span className="text-yellow-300/80 text-[11px]">Awaiting host approval</span>
        )}
        {(slot.status === 'claimed' || slot.status === 'pending') && (isMine || isHost) && (
          <button onClick={() => onRelease(slot.index)} disabled={busy} className="bg-gray-500/15 hover:bg-red-500/20 border border-gray-500/40 text-gray-300 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-medium" data-testid={`planner-release-${slot.index}`}>
            {isMine && !isHost ? 'Drop' : 'Remove'}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Edit Wall Announcement modal (host) ────────────────────
const EditAnnouncementModal = ({ eventId, onClose, onSaved, onChanged }) => {
  const [caption, setCaption] = useState('');
  const [originalCaption, setOriginalCaption] = useState('');
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aiTone, setAiTone] = useState('auto');
  const [aiNotes, setAiNotes] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/announcement`, { headers: authHeaders() });
        const data = await res.json();
        if (res.ok) { setCaption(data.caption || ''); setOriginalCaption(data.original_caption || ''); }
        else if (res.status === 404) setMissing(true);
        else setError(data.detail || 'Could not load the announcement');
      } catch { setError('Could not load the announcement'); }
      setLoading(false);
    })();
  }, [eventId]);

  const repost = async () => {
    setReposting(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/announcement/repost`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setCaption(data.caption || '');
        setOriginalCaption(data.caption || '');
        setMissing(false);
        toast({ title: '📣 Announcement re-posted', description: 'Your event is back on The Wall.' });
        onChanged?.();
      } else setError(data.detail || 'Could not re-post — try again');
    } catch { setError('Could not re-post — try again'); }
    setReposting(false);
  };

  const rewriteAi = async () => {
    setAiLoading(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/announcement/ai`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ tone: aiTone, notes: aiNotes }),
      });
      const data = await res.json();
      if (res.ok) setCaption(data.caption || '');
      else setError(data.detail || 'AI rewrite failed — try again');
    } catch { setError('AI rewrite failed — try again'); }
    setAiLoading(false);
  };

  const save = async () => {
    if (!caption.trim()) { setError("Announcement text can't be empty"); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/announcement`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ caption }),
      });
      const data = await res.json();
      if (res.ok) { onSaved?.(); onClose(); }
      else setError(data.detail || 'Failed to save');
    } catch { setError('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="planner-edit-announcement-modal">
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col bg-[#141428] border border-fuchsia-500/40 rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-fuchsia-500/20 shrink-0">
          <h3 className="text-white font-bold text-sm">📣 Edit Wall Announcement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="planner-edit-announcement-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">
          {loading ? (
            <div className="text-gray-500 text-sm py-6 text-center">Loading…</div>
          ) : missing ? (
            <div className="text-center py-6 space-y-3" data-testid="planner-announcement-missing">
              <div className="text-4xl">📣</div>
              <p className="text-gray-300 text-sm">This event isn't on The Wall right now — its announcement was removed. Re-post it so DJs can find the event and claim slots.</p>
              {error && <div className="text-red-400 text-xs" data-testid="planner-edit-announcement-error">{error}</div>}
              <button
                onClick={repost}
                disabled={reposting}
                className="w-full bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm"
                data-testid="planner-announcement-repost"
              >
                {reposting ? 'Posting…' : '📣 Post to Wall'}
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-400 text-xs mb-3">This is the post announcing your event on The Wall. Tweak the wording — changes show instantly on your existing post.</p>
              <div className="bg-cyan-900/10 border border-cyan-700/30 rounded-lg p-3 mb-3 space-y-2" data-testid="planner-announcement-ai-panel">
                <div className="text-[11px] font-semibold text-cyan-300">✨ Rewrite with AI</div>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { v: 'auto', l: '✨ Auto' },
                    { v: 'hype', l: '🔥 Hype' },
                    { v: 'professional', l: '🎩 Professional' },
                    { v: 'chill', l: '😎 Chill' },
                  ].map(t => (
                    <button
                      key={t.v}
                      onClick={() => setAiTone(t.v)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-medium transition-colors ${aiTone === t.v ? 'bg-cyan-500/40 border-cyan-400 text-white' : 'bg-gray-800/60 border-gray-700/50 text-gray-300 hover:border-cyan-500/50'}`}
                      data-testid={`planner-announcement-tone-${t.v}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
                <input
                  className={inputCls}
                  value={aiNotes}
                  onChange={e => setAiNotes(e.target.value)}
                  placeholder="Anything to emphasize? (optional — e.g., free drinks, guest headliner…)"
                  data-testid="planner-announcement-ai-notes"
                />
                <button
                  onClick={rewriteAi}
                  disabled={aiLoading}
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg"
                  data-testid="planner-announcement-ai-generate"
                >
                  {aiLoading ? '✍️ Writing…' : '✨ Rewrite with AI'}
                </button>
              </div>
              <textarea rows={7} className={inputCls} value={caption} onChange={e => setCaption(e.target.value)} maxLength={1000} data-testid="planner-edit-announcement-input" />
              <div className="flex items-center justify-between mt-1">
                {originalCaption && caption.trim() !== originalCaption.trim() ? (
                  <button
                    onClick={() => { setCaption(originalCaption); setError(''); }}
                    className="text-[11px] text-amber-300 hover:text-amber-200 font-medium flex items-center gap-1"
                    data-testid="planner-announcement-revert"
                  >
                    ↩️ Revert to original wording
                  </button>
                ) : <span />}
                <span className="text-[11px] text-gray-500">{caption.length}/1000</span>
              </div>
              {error && <div className="text-red-400 text-xs mt-1" data-testid="planner-edit-announcement-error">{error}</div>}
            </>
          )}
        </div>
        {!loading && !missing && (
          <div className="shrink-0 border-t border-fuchsia-500/20 bg-[#141428] px-4 sm:px-5 py-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
            <button onClick={save} disabled={saving || loading} className="w-full bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm" data-testid="planner-edit-announcement-save">
              {saving ? 'Saving…' : '💾 Save Announcement'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Event detail ───────────────────────────────────────────
const EventDetail = ({ eventId, me, onBack, onDeleted }) => {
  const [event, setEvent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignSlot, setAssignSlot] = useState(null);
  const [showAiPoster, setShowAiPoster] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditAnnouncement, setShowEditAnnouncement] = useState(false);
  const [viewTz, setViewTz] = useState('event');
  const dragFrom = React.useRef(null);
  const canClaim = me && (me.role === 'admin' || [...typeTokens(me)].some(t => ['dj', 'streamer'].includes(t)));
  const isHost = me && event && (event.host_id === me.user_id || me.role === 'admin');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}`, { headers: authHeaders() });
      if (res.ok) setEvent((await res.json()).event);
    } catch (e) { console.error('planner detail load:', e); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const slotAction = async (action, index) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/slots/${index}/${action}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) alert(data.detail || 'Action failed');
      await load();
    } catch { alert('Action failed'); }
    setBusy(false);
  };

  const dragHandlers = {
    onDragStart: (index) => { dragFrom.current = index; },
    onDrop: async (toIndex) => {
      const fromIndex = dragFrom.current;
      dragFrom.current = null;
      if (fromIndex === null || fromIndex === toIndex || !event) return;
      const order = event.slots.map(s => s.index);
      order.splice(toIndex, 0, order.splice(fromIndex, 1)[0]);
      setBusy(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/slots/reorder`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ order }),
        });
        const data = await res.json();
        if (!res.ok) alert(data.detail || 'Failed to reorder');
        await load();
      } catch { alert('Failed to reorder'); }
      setBusy(false);
    },
  };

  const assignUser = async (u) => {
    if (!assignSlot) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/slots/${assignSlot.index}/assign`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ user_id: u.id }),
      });
      const data = await res.json();
      if (!res.ok) alert(data.detail || 'Failed to assign');
      setAssignSlot(null);
      await load();
    } catch { alert('Failed to assign'); }
    setBusy(false);
  };

  const deleteEvent = async () => {
    if (!window.confirm('Delete this event and its calendar entry?')) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) onDeleted();
      else alert('Failed to delete');
    } catch { alert('Failed to delete'); }
    setBusy(false);
  };

  if (!event) return <div className="text-gray-400 text-sm p-8 text-center">Loading event…</div>;

  const isDateTbd = event.date_tbd || !event.event_date;

  // Build the date display line — handles TBD + multi-day
  let dateStr;
  if (event.date_tbd || !event.event_date) {
    dateStr = '📅 Date coming soon';
  } else {
    const startFmt = new Date(`${event.event_date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
    if (event.event_end_date && event.event_end_date !== event.event_date) {
      const endFmt = new Date(`${event.event_end_date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
      dateStr = `${startFmt} – ${endFmt}`;
    } else {
      dateStr = startFmt;
    }
  }
  const timeStr = (event.time_tbd || !event.start_time)
    ? '🕘 Start time coming soon'
    : `${event.slots?.[0]?.start_label || ''} ${event.timezone_label || ''}`;
  const scheduleChipLabel = event.schedule_type === 'both'
    ? '🔁 Weekday + Weekend'
    : event.schedule_type === 'weekday' ? '📆 Weekday'
    : event.schedule_type === 'weekend' ? '🎉 Weekend'
    : null;

  // Group slots into 24-hour day blocks — "Day 1 / Day 2 / …"
  const slotGroups = [];
  if (event.slots?.length) {
    event.slots.forEach(slot => {
      const day = Math.floor(slot.index / 24);
      if (!slotGroups.length || slotGroups[slotGroups.length - 1].day !== day) {
        slotGroups.push({ day, slots: [] });
      }
      slotGroups[slotGroups.length - 1].slots.push(slot);
    });
  }
  const showDayHeaders = slotGroups.length > 1;

  const toggleInterest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${eventId}/interest`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) alert(data.detail || 'Failed');
      await load();
    } catch { alert('Failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="planner-event-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-purple-300 hover:text-white text-sm" data-testid="planner-detail-back">
        <ArrowLeft size={16} /> All Events
      </button>

      {/* Header */}
      <div className="relative bg-gradient-to-br from-purple-900/40 to-fuchsia-900/20 border border-purple-500/30 rounded-xl p-5 overflow-hidden">
        {event.flyer_url && (
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${event.flyer_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        )}
        <div className="relative">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-purple-500/20 border border-purple-500/50 text-purple-200">{TYPE_LABELS[event.event_type] || event.event_type}</span>
              <h2 className="text-white text-2xl font-bold mt-2">{event.title}</h2>
              <div className="text-gray-300 text-sm mt-1 flex items-center gap-3 flex-wrap">
                <span data-testid="planner-detail-date">📅 {dateStr}</span>
                <span data-testid="planner-detail-time">🕘 {timeStr}</span>
                {scheduleChipLabel && <span className="text-cyan-300 text-xs font-semibold px-2 py-0.5 rounded-md border border-cyan-500/40 bg-cyan-500/10" data-testid="planner-detail-schedule-chip">{scheduleChipLabel}</span>}
                {event.genres && <span>🎵 {event.genres}</span>}
              </div>
              <div className="text-gray-400 text-xs mt-2 flex items-center gap-3 flex-wrap">
                {event.event_type === 'stream' ? (
                  <span className="flex items-center gap-1"><Radio size={12} /> {event.platform || 'Online'}{event.channel_link && <a href={event.channel_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline ml-1">{event.channel_link}</a>}</span>
                ) : (
                  <span className="flex items-center gap-1"><MapPin size={12} /> {event.venue_name || (event.hide_venue ? '🔒 Private venue' : 'TBA')}{event.city ? ` · ${event.city}` : ''}</span>
                )}
                <span className="flex items-center gap-1"><Users size={12} /> Hosted by <span className="text-white font-medium">{event.host_name}</span></span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {isDateTbd && canClaim && (
                <button
                  onClick={toggleInterest}
                  disabled={busy}
                  className={`px-3 py-1.5 rounded text-xs font-medium border flex items-center gap-1.5 disabled:opacity-40 ${event.viewer_interested ? 'bg-amber-500/25 border-amber-400 text-amber-100' : 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/40 text-amber-200'}`}
                  data-testid="planner-interest-bell-btn"
                  title="Get pinged the moment the date locks in"
                >
                  <Bell size={12} /> {event.viewer_interested ? "You'll get pinged" : 'Notify me'} · {event.interest_count || 0}
                </button>
              )}
              {isDateTbd && !canClaim && (event.interest_count || 0) > 0 && (
                <span className="text-amber-300/80 text-[11px]" data-testid="planner-interest-count">🔔 {event.interest_count} DJ{event.interest_count === 1 ? '' : 's'} waiting on the date</span>
              )}
              {isHost && event.invite_token && (
                <button
                  onClick={() => setShowInvite(true)}
                  className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/50 text-amber-200 px-3 py-1.5 rounded text-xs font-medium"
                  data-testid="planner-invite-link-btn"
                >
                  🔗 Invite Link
                </button>
              )}
              <button
                onClick={() => window.open(`${BACKEND_URL}/api/planner/events/${eventId}/poster`, '_blank')}
                className="bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/50 text-cyan-200 px-3 py-1.5 rounded text-xs font-medium"
                data-testid="planner-poster-btn"
              >
                📸 Lineup Poster
              </button>
              {isHost && (
                <button
                  onClick={() => setShowAiPoster(true)}
                  className="bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/50 text-purple-200 px-3 py-1.5 rounded text-xs font-medium"
                  data-testid="planner-ai-poster-btn"
                >
                  ✨ AI Poster
                </button>
              )}
              {isHost && (
                <button
                  onClick={() => setShowEditAnnouncement(true)}
                  className="bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-500/50 text-fuchsia-200 px-3 py-1.5 rounded text-xs font-medium"
                  data-testid="planner-edit-announcement-btn"
                >
                  {event.wall_post_id ? '📣 Edit Post' : '📣 Post to Wall'}
                </button>
              )}
              {isHost && (
                <button onClick={deleteEvent} disabled={busy} className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-200 px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5" data-testid="planner-delete-event">
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          </div>
          {event.description && <p className="text-gray-300 text-sm mt-3 whitespace-pre-line">{event.description}</p>}
          {isHost && isDateTbd && (event.interested?.length || 0) > 0 && (
            <div className="mt-3" data-testid="planner-interest-names">
              <div className="text-amber-300 text-xs font-semibold mb-1.5">🔔 Waiting for the date ({event.interested.length})</div>
              <div className="flex gap-1.5 flex-wrap">
                {event.interested.map(p => (
                  <span key={p.user_id} className="text-[11px] px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200">{p.name}</span>
                ))}
              </div>
            </div>
          )}
          {(event.external_link || event.discord_link) && (
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              {event.external_link && <a href={event.external_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-xs hover:underline flex items-center gap-1"><Link2 size={12} /> Event Link</a>}
              {event.discord_link && <a href={event.discord_link} target="_blank" rel="noopener noreferrer" className="text-indigo-400 text-xs hover:underline flex items-center gap-1"><Link2 size={12} /> Discord</a>}
            </div>
          )}
        </div>
      </div>

      {/* Host-only announcement analytics */}
      {isHost && event.analytics && (
        <div className="bg-gradient-to-r from-fuchsia-900/15 to-purple-900/10 border border-fuchsia-500/25 rounded-xl p-3" data-testid="planner-analytics-strip">
          <div className="text-[11px] font-semibold text-fuchsia-300 mb-2 flex items-center gap-1.5">📊 Announcement reach</div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center bg-black/20 rounded-lg py-2" data-testid="planner-analytics-views">
              <div className="text-white text-lg font-bold">{event.analytics.views}</div>
              <div className="text-gray-400 text-[10px] uppercase tracking-wide">Viewed</div>
            </div>
            <div className="text-center bg-black/20 rounded-lg py-2" data-testid="planner-analytics-clicks">
              <div className="text-white text-lg font-bold">{event.analytics.announcement_clicks}</div>
              <div className="text-gray-400 text-[10px] uppercase tracking-wide">Wall clicks</div>
            </div>
            <div className="text-center bg-black/20 rounded-lg py-2" data-testid="planner-analytics-claimed">
              <div className="text-white text-lg font-bold">{event.analytics.claimed_slots}<span className="text-gray-500 text-xs font-normal">/{event.analytics.total_slots}</span></div>
              <div className="text-gray-400 text-[10px] uppercase tracking-wide">Claimed</div>
            </div>
          </div>
          <p className="text-gray-500 text-[10px] mt-2">DJs who opened this event · click-throughs from your Wall post · slots taken so far.</p>
        </div>
      )}

      {/* Timetable */}
      <div>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-white font-bold text-sm">🎛️ Time Table</h3>
            {eventHasRealTime(event) && (
              <select
                value={viewTz}
                onChange={e => setViewTz(e.target.value)}
                className="bg-gray-800/60 border border-gray-700/50 rounded-lg px-2 py-1 text-xs text-cyan-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="planner-tz-select"
                title="View slot times in a different timezone"
              >
                <option value="event">🌐 Event time ({event.timezone_label})</option>
                <option value="local">📍 My local time</option>
                {TZ_CHOICES.filter(tz => tz !== event.timezone_label).map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            )}
            {isHost && (
              <button onClick={() => setShowEdit(true)} disabled={busy} className="text-[11px] px-2.5 py-1 rounded-lg bg-gray-800/60 hover:bg-purple-800/40 border border-gray-700/50 hover:border-purple-500/60 text-gray-300 hover:text-white font-medium disabled:opacity-40 flex items-center gap-1" data-testid="planner-edit-event-btn" title="Edit event details, dates and days">
                <Pencil size={11} /> Edit Event
              </button>
            )}
          </div>
          <span className="text-xs text-gray-400">
            {event.slots.filter(s => s.status === 'available').length} of {event.slots.length} slots open · max 2/DJ per day · {event.claim_mode === 'open' ? '🟢 open claiming' : event.claim_mode === 'request' ? '🟡 request to join' : '🔒 invite-only'}
          </span>
        </div>
        <div className="space-y-2">
          {slotGroups.map((group, gi) => (
            <React.Fragment key={`day-${group.day}-${gi}`}>
              {showDayHeaders && (
                <div className="flex items-center gap-2 pt-2 first:pt-0" data-testid={`planner-day-header-${group.day}`}>
                  <div className="text-xs font-bold text-amber-300 uppercase tracking-wider">Day {group.day + 1}</div>
                  <div className="flex-1 h-px bg-amber-500/20"></div>
                  <span className="text-[11px] text-gray-500">{group.slots.length} slot{group.slots.length === 1 ? '' : 's'}</span>
                </div>
              )}
              {group.slots.map(slot => (
                <SlotRow
                  key={slot.index}
                  slot={slot} event={event} me={me} canClaim={canClaim} busy={busy} viewTz={viewTz}
                  onClaim={i => slotAction('claim', i)}
                  onApprove={i => slotAction('approve', i)}
                  onDeny={i => slotAction('deny', i)}
                  onRelease={i => slotAction('release', i)}
                  onAssignOpen={setAssignSlot}
                  dragHandlers={dragHandlers}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
        {!canClaim && <p className="text-gray-500 text-xs mt-2">Only DJ and Streamer accounts can claim slots.</p>}
        {me && event && (event.host_id === me.user_id || me.role === 'admin') && (
          <p className="text-gray-600 text-[11px] mt-2">Tip: drag the ⋮⋮ handle to move a DJ to a different time slot.</p>
        )}
      </div>

      {assignSlot && (
        <AssignModal slot={assignSlot} onClose={() => setAssignSlot(null)} onAssign={assignUser} busy={busy} />
      )}
      {showAiPoster && (
        <AiPosterModal eventId={eventId} onClose={() => setShowAiPoster(false)} />
      )}
      {showInvite && (
        <InviteLinkModal event={event} dateStr={dateStr} me={me} onClose={() => setShowInvite(false)} />
      )}
      {showEdit && (
        <CreateEventModal
          initial={event}
          eventId={eventId}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); load(); }}
        />
      )}
      {showEditAnnouncement && (
        <EditAnnouncementModal
          eventId={eventId}
          onClose={() => setShowEditAnnouncement(false)}
          onChanged={load}
          onSaved={() => { toast({ title: '📣 Announcement updated', description: 'Your Wall post now shows the new wording.' }); load(); }}
        />
      )}
    </div>
  );
};

// ── Main tab ───────────────────────────────────────────────
const EventPlannerTab = ({ onBack }) => {
  const me = getMe();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [listTz, setListTz] = useState('event');
  const [scheduleFilter, setScheduleFilter] = useState('all'); // all | weekday | weekend
  const canCreate = me && (me.role === 'admin' || [...typeTokens(me)].some(t => ['dj', 'producer', 'streamer', 'label', 'ar', 'venue_owner', 'promoter'].includes(t)));
  const canClaim = me && (me.role === 'admin' || [...typeTokens(me)].some(t => ['dj', 'streamer'].includes(t)));

  const toggleCardInterest = async (ev, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events/${ev.id}/interest`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) alert(data.detail || 'Failed');
      load();
    } catch { alert('Failed'); }
  };

  const visibleEvents = events.filter(ev => scheduleFilter === 'all' || ev.schedule_type === scheduleFilter || ev.schedule_type === 'both');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/planner/events`, { headers: authHeaders() });
      if (res.ok) setEvents((await res.json()).events || []);
    } catch (e) { console.error('planner list load:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link from invite banner: open a specific event
  useEffect(() => {
    const h = (e) => {
      const id = e.detail?.eventId;
      if (id) setDetailId(id);
    };
    window.addEventListener('plannerOpenEvent', h);
    return () => window.removeEventListener('plannerOpenEvent', h);
  }, []);

  return (
    <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: '75vh' }} data-testid="event-planner-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-purple-300 hover:text-white text-sm" data-testid="planner-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <h2 className="text-white text-xl font-bold">🗓️ Event Planner</h2>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2" data-testid="planner-new-event-btn">
            <CalendarPlus size={16} /> Plan an Event
          </button>
        )}
      </div>

      {detailId ? (
        <EventDetail eventId={detailId} me={me} onBack={() => { setDetailId(null); load(); }} onDeleted={() => { setDetailId(null); load(); }} />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <p className="text-gray-400 text-sm">Build a DJ lineup for your stream, club night, bar, lounge or private event — DJs claim their slots and the timetable fills itself.</p>
            <div className="flex items-center gap-2 flex-wrap min-w-0 max-w-full">
              <div className="flex gap-1 flex-wrap" data-testid="planner-schedule-filter">
                {[
                  { v: 'all', l: 'All' },
                  { v: 'weekday', l: '📆 Weekday' },
                  { v: 'weekend', l: '🎉 Weekend' },
                ].map(f => (
                  <button
                    key={f.v}
                    onClick={() => setScheduleFilter(f.v)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-colors ${scheduleFilter === f.v ? 'bg-cyan-500/25 border-cyan-400 text-cyan-100' : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:border-cyan-500/50 hover:text-cyan-200'}`}
                    data-testid={`planner-filter-${f.v}`}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
              <select
                value={listTz}
                onChange={e => setListTz(e.target.value)}
                className="bg-gray-800/60 border border-gray-700/50 rounded-lg px-2 py-1.5 text-xs text-cyan-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 min-w-0"
                data-testid="planner-list-tz-select"
                title="View event times in a different timezone"
              >
                <option value="event">🌐 Event time</option>
                <option value="local">📍 My local time</option>
                {TZ_CHOICES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16" data-testid="planner-empty-state">
              <div className="text-5xl mb-3">🗓️</div>
              <p className="text-gray-400 text-sm">No lineup events yet.{canCreate ? ' Be the first to plan one!' : ''}</p>
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="text-center py-16" data-testid="planner-filter-empty-state">
              <div className="text-5xl mb-3">{scheduleFilter === 'weekend' ? '🎉' : '📆'}</div>
              <p className="text-gray-400 text-sm">No {scheduleFilter} events right now — check back soon or view All.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleEvents.map(ev => {
                const st = eventStartInTz(ev, listTz);
                const evScheduleChip = ev.schedule_type === 'both'
                  ? '🔁 Wknd + Wkday'
                  : ev.schedule_type === 'weekday' ? '📆 Weekday'
                  : ev.schedule_type === 'weekend' ? '🎉 Weekend'
                  : null;
                const dateTimeLine = st.tbd
                  ? (st.time ? `📅 ${st.dateStr} · 🕘 ${st.time}` : `📅 ${st.dateStr}`)
                  : `📅 ${st.dateStr} · 🕘 ${st.time} ${st.tz}`;
                return (
                  <div
                    key={ev.id}
                    onClick={() => setDetailId(ev.id)}
                    className="bg-gray-800/40 border border-gray-700/40 hover:border-purple-500/60 rounded-xl p-4 cursor-pointer transition-all hover:-translate-y-0.5"
                    data-testid={`planner-event-card-${ev.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/20 border border-purple-500/50 text-purple-200">{TYPE_LABELS[ev.event_type] || ev.event_type}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${ev.open_slots > 0 ? 'bg-green-500/15 border border-green-500/40 text-green-300' : 'bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300'}`}>
                        {ev.open_slots > 0 ? `${ev.open_slots} slot${ev.open_slots === 1 ? '' : 's'} open` : 'Lineup full'}
                      </span>
                    </div>
                    <h3 className="text-white font-bold mt-2 truncate">{ev.title}</h3>
                    <div className="text-gray-400 text-xs mt-1" data-testid={`planner-card-datetime-${ev.id}`}>{dateTimeLine}</div>
                    {(ev.date_tbd || !ev.event_date) && (
                      <div className="mt-1.5 flex items-center gap-2">
                        {canClaim ? (
                          <button
                            onClick={(e) => toggleCardInterest(ev, e)}
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border flex items-center gap-1 ${ev.viewer_interested ? 'bg-amber-500/25 border-amber-400 text-amber-100' : 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/40 text-amber-200'}`}
                            data-testid={`planner-card-bell-${ev.id}`}
                            title="Get pinged the moment the date locks in"
                          >
                            <Bell size={10} /> {ev.viewer_interested ? "You'll get pinged" : 'Notify me'} · {ev.interest_count || 0}
                          </button>
                        ) : (ev.interest_count || 0) > 0 && (
                          <span className="text-amber-300/80 text-[10px]">🔔 {ev.interest_count} waiting</span>
                        )}
                      </div>
                    )}
                    {evScheduleChip && (
                      <div className="mt-1.5">
                        <span className="text-cyan-300 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10" data-testid={`planner-card-schedule-${ev.id}`}>{evScheduleChip}</span>
                      </div>
                    )}
                    <div className="text-gray-500 text-xs mt-1 truncate">
                      {ev.event_type === 'stream' ? `📡 ${ev.platform || 'Online'}` : `📍 ${ev.venue_name || (ev.hide_venue ? '🔒 Private venue' : 'TBA')}${ev.city ? ` · ${ev.city}` : ''}`}
                    </div>
                    <div className="text-gray-500 text-xs mt-2">Hosted by <span className="text-gray-300">{ev.host_name}</span> · {ev.total_slots} slots</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(ev) => {
            setShowCreate(false);
            load();
            const t = toast({
              title: '🎉 Event Published!',
              description: `"${ev.title}" is live — announced on The Wall and added to the calendar.`,
              duration: 10000,
              action: (
                <div className="flex flex-col gap-1.5 shrink-0">
                  <ToastAction
                    altText="View lineup"
                    onClick={() => window.dispatchEvent(new CustomEvent('gigBoardOpenPlanner', { detail: { eventId: ev.id } }))}
                    className="bg-purple-600 hover:bg-purple-500 text-white border-0"
                    data-testid="planner-toast-view-lineup"
                  >
                    View lineup
                  </ToastAction>
                  <ToastAction
                    altText="Undo publish"
                    onClick={async () => {
                      try {
                        await fetch(`${BACKEND_URL}/api/planner/events/${ev.id}`, { method: 'DELETE', headers: authHeaders() });
                      } catch { /* noop */ }
                      t.dismiss();
                      load();
                      toast({ title: '↩️ Event removed', description: `"${ev.title}" was deleted.` });
                    }}
                    className="bg-transparent hover:bg-red-500/20 border border-red-500/50 text-red-200"
                    data-testid="planner-toast-undo"
                  >
                    Undo
                  </ToastAction>
                </div>
              ),
            });
            onBack();
          }}
        />
      )}
    </div>
  );
};

export default EventPlannerTab;
